import { useContext } from 'react';
import { UserContext } from '../context/UserContext';
import { BrandMemberRole } from '../types/enums';
import useSubscription from './useSubscription';

// Type qui définit les niveaux d'accès possibles
export type AccessLevel = 'free' | 'limited' | string;

/**
 * Custom hook to check user's brand access permissions
 */
const useBrandAccess = () => {
  const { user } = useContext(UserContext) || {};
  const { hasSubscription } = useSubscription();

  /**
   * Check if a feature is accessible to the brand user
   * @param featureAccess Access level required for the feature ('free' or 'limited')
   * @returns Boolean indicating if the user can access the feature
   */
  const canAccessFeature = (featureAccess: AccessLevel): boolean => {
    if (!user) return false;

    // Admin/manager check - they have full access
    if (
      user.brandMemberRole !== undefined &&
      user.brandMemberRole !== BrandMemberRole.User
    ) {
      return true;
    }

    // Access based on feature type
    if (featureAccess === 'free') {
      return true;
    }

    // Limited features should always be shown, with premium icon if no subscription
    // (we return true to show the menu item, subscription check is done in the sidebar component)
    if (featureAccess === 'limited') {
      return true;
    }

    return false;
  };

  /**
   * Check if user is a brand admin (not regular User role)
   */
  const isBrandAdmin = (): boolean => {
    if (!user || user.brandMemberRole === undefined) return false;
    return user.brandMemberRole !== BrandMemberRole.User;
  };

  /**
   * Check if user has any brand member role
   */
  const isBrandMember = (): boolean => {
    if (!user) return false;
    return user.brandMemberRole !== undefined;
  };

  return {
    canAccessFeature,
    isBrandMember,
    isBrandAdmin,
  };
};

export default useBrandAccess;
